<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-4xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          <span class="text-varroc-accent">iQ360</span>
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-3xl" data-aos="fade-up" data-aos-delay="200">
          Deliver the right message at the right time through a scalable, event-driven messaging architecture. iQ360 transforms high-volume, real-time data streams into actionable, personalized communication that improves engagement, safety outcomes, and proactive service orchestration.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all context_based_messaging_intelligence_talk_to_our_team_button">Talk to Our Team</a>
        </div>
      </div>
    </div>
  </section>

  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">
    <section id="overview" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">What It Does</span>
          <h2 class="text-4xl font-black mt-3 mb-5">A Real-Time Decisioning and Communication Layer</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-5">iQ360 functions as a real-time decisioning and communication layer built on top of telematics and connected system data. It ingests, processes, and evaluates multi-source streams to trigger contextually relevant notifications across multiple delivery channels.</p>
          <p class="text-varroc-text-gray leading-relaxed">The result is communication that is precise, scalable, and operationally useful, enabling personalized engagement and action execution across high-volume connected ecosystems.</p>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-4">At a Glance</h3>
          <ul class="space-y-3">
            <li *ngFor="let item of highlights" class="flex items-start gap-3 text-varroc-text-gray text-sm">
              <i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="capabilities" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Capabilities</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Built for Scale, Speed, and Precision</h2>
        <p class="text-varroc-text-gray max-w-3xl mr-auto">The system turns raw connected data into meaningful, contextual communication by combining event detection, segmentation, rule evaluation, and orchestration into one messaging layer.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div *ngFor="let capability of capabilities; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-2xl' [class]="capability.icon"></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">{{ capability.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ capability.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="pipeline" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">How It Works</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Stream Processing and Rule Evaluation Pipeline</h2>
        <p class="text-varroc-text-gray max-w-3xl mr-auto">Data Sources → Data Lake → Event Detection → User Segmentation → Rule Evaluation → Action Execution through API, SMS, Email, or Push.</p>
      </div>
      <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div *ngFor="let step of pipelineSteps; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="flex items-center gap-3 mb-5">
            <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i class='bx icon-gradient text-2xl' [class]="step.icon"></i>
            </div>
            <div>
              <h3 class="text-white font-bold text-lg">{{ step.title }}</h3>
            </div>
          </div>
          <ul class="space-y-3">
            <li *ngFor="let point of step.points" class="flex items-start gap-3 text-varroc-text-gray text-sm">
              <i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="actions" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Action & Orchestration Layer</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Beyond Messaging into Real Action</h2>
        <p class="text-varroc-text-gray max-w-3xl mr-auto">Once context is evaluated, the system can trigger communication, APIs, and downstream workflows so engagement becomes operationally meaningful rather than purely informational.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div *ngFor="let action of actionChannels; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-2xl' [class]="action.icon"></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">{{ action.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ action.desc }}</p>
        </div>
      </div>
    </section>

    <!-- <section class="text-center py-16 bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-3xl" data-aos="fade-up">
      <h2 class="text-4xl font-black mb-4">Deliver Intelligent Communication at Scale</h2>
      <p class="text-varroc-text-gray text-lg mb-8 max-w-3xl mx-auto">Use iQ360 to turn connected mobility data into relevant engagement, proactive alerts, and orchestrated service actions across channels.</p>
      <div class="flex flex-wrap gap-4 justify-center">
        <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-10 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all text-lg">Talk to Team</a>
        <a routerLink="/products/connectiq" class="border border-varroc-accent/40 font-semibold px-10 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all text-lg">Back to ConnectIQ</a>
      </div>
    </section> -->
  </div>
</main>